import React, { useMemo } from 'react';

import { getFileFieldDisplayName } from '@aline/graphql';
import { Typography } from '@mui/material';
import { GenericPropertyIcon } from 'src/components/icons/GenericPropertyIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  chip: {
    alignItems: 'center',
    backgroundColor: theme.appColors.selectedBackground,
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
  },
  chipText: {
    fontSize: 12,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

type QuestionInput = Pick<Apollo.ExtractionQuestion, 'id' | 'title' | '__typename'>;

interface QuestionInputChipsProps {
  inputs: QuestionInput[];
  inputFileFields?: Apollo.ReportColumnFileField[];
}

export function QuestionInputChips({
  inputFileFields = [],
  inputs,
}: QuestionInputChipsProps): JSX.Element {
  const { classes } = useStyles();
  const sortedChips = useMemo(() => {
    const allChips: { input: QuestionInputChipProps['input']; key: string; label: string }[] = [
      ...inputs.map(q => ({ input: q, key: q.id, label: q.title || 'Untitled' })),
      ...inputFileFields.map(f => ({
        input: { field: f },
        key: f,
        label: getFileFieldDisplayName(f),
      })),
    ];
    return allChips.sort((a, b) => a.label.localeCompare(b.label));
  }, [inputFileFields, inputs]);

  if (!sortedChips.length) return <QuestionInputChip input="file" />;

  return (
    <div className={classes.chips}>
      {sortedChips.map(chip => (
        <QuestionInputChip key={chip.key} input={chip.input} />
      ))}
    </div>
  );
}

interface QuestionInputChipProps {
  input: QuestionInput | 'file' | { field: Apollo.ReportColumnFileField };
}

export function QuestionInputChip({ input }: QuestionInputChipProps): JSX.Element {
  const { classes } = useStyles();

  if (input === 'file')
    return (
      <div className={classes.chip}>
        <QuestionInputChipIcon variant="file" />
        <Typography noWrap className={classes.chipText} color="primary">
          File
        </Typography>
      </div>
    );

  if ('field' in input)
    return (
      <div className={classes.chip}>
        <QuestionInputChipIcon variant="file" />
        <Typography noWrap className={classes.chipText} color="primary">
          {getFileFieldDisplayName(input.field)}
        </Typography>
      </div>
    );

  return (
    <div className={classes.chip}>
      <QuestionInputChipIcon variant="ai" />
      <Typography noWrap className={classes.chipText} color="primary">
        {input.title || 'Untitled'}
      </Typography>
    </div>
  );
}

const useIconStyles = makeStyles()(_theme => ({
  icon: {
    height: 12,
    width: 12,
  },
}));

interface QuestionInputChipIconProps {
  variant: 'ai' | 'file';
}

function QuestionInputChipIcon({ variant }: QuestionInputChipIconProps): JSX.Element {
  const { classes } = useIconStyles();

  switch (variant) {
    case 'ai':
      return <SmallAiSparkleIcon className={classes.icon} fill={IconColors.Primary} />;
    case 'file':
      return <GenericPropertyIcon className={classes.icon} />;
  }
}
